import { useEffect, useRef, useState } from "react";
import { Save, QrCode, Printer, Copy, KeyRound, Volume2 } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import MediaUpload from "@/components/admin/MediaUpload";

export default function EditSettings() {
  const [s, setS] = useState(null);
  const { user, applyAuth } = useAuth();
  const qrRef = useRef(null);
  const siteBase = (s?.public_site_url || "").trim().replace(/\/+$/, "") || (typeof window !== "undefined" ? window.location.origin : "");
  const guestUrl = siteBase;

  const previewChime = () => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) { toast.error("Your browser can't play sound here."); return; }
    const ctx = new AC();
    const t = ctx.currentTime;
    [880, 1174.66, 880].forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      const t0 = t + i * 0.22;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.55, t0 + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.38);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.42);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  };

  const [cred, setCred] = useState({ current_password: "", new_email: "", new_password: "", confirm_password: "" });
  const [savingCred, setSavingCred] = useState(false);
  const setC = (k, v) => setCred((c) => ({ ...c, [k]: v }));

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user?.email) setCred((c) => ({ ...c, new_email: user.email })); }, [user]);

  const saveCredentials = async () => {
    if (!cred.current_password) { toast.error("Enter your current password"); return; }
    if (cred.new_password && cred.new_password !== cred.confirm_password) { toast.error("New passwords do not match"); return; }
    const emailChanged = cred.new_email && cred.new_email.trim().toLowerCase() !== (user?.email || "").toLowerCase();
    if (!emailChanged && !cred.new_password) { toast.error("Enter a new email or a new password to update"); return; }
    setSavingCred(true);
    try {
      const { data } = await api.post("/auth/change-credentials", {
        current_password: cred.current_password,
        new_email: emailChanged ? cred.new_email.trim() : null,
        new_password: cred.new_password || null,
      });
      applyAuth(data);
      toast.success("Login updated successfully");
      setCred({ current_password: "", new_email: data.user.email, new_password: "", confirm_password: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update login");
    } finally {
      setSavingCred(false);
    }
  };

  const save = async () => { await api.put("/settings", s); toast.success("Settings saved"); };

  const printQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const w = window.open("", "_blank", "width=600,height=800");
    w.document.write(`
      <html><head><title>Guidebook QR</title>
      <style>
        body{font-family:Georgia,serif;text-align:center;padding:60px 40px;color:#2C3B2E}
        h1{font-size:34px;margin:0 0 6px}
        p{color:#666;font-size:15px;margin:0 0 30px;font-family:Arial,sans-serif}
        img{width:320px;height:320px;border:12px solid #F7F5F0;border-radius:16px}
        .cta{margin-top:26px;font-family:Arial,sans-serif;font-size:16px;color:#C47053;font-weight:bold}
        .url{margin-top:8px;font-family:Arial,sans-serif;font-size:12px;color:#999}
      </style></head>
      <body>
        <h1>${s?.property_name || "Welcome"}</h1>
        <p>${s?.tagline || "Scan for the digital guidebook"}</p>
        <img src="${dataUrl}" />
        <div class="cta">Scan to open the guidebook on your phone</div>
        <div class="url">${guestUrl}</div>
        <script>window.onload=function(){window.print()}</script>
      </body></html>`);
    w.document.close();
  };

  if (!s) return null;
  const set = (k, v) => setS({ ...s, [k]: v });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><span className="overline text-brand-terracotta">Edit</span><h1 className="font-display text-4xl tracking-tight mt-1">Settings</h1></div>
        <Button onClick={save} data-testid="save-settings" className="rounded-full bg-brand-forest hover:bg-brand-terracotta text-white active:scale-95 transition-colors"><Save size={16} className="mr-2" />Save</Button>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm space-y-5 max-w-2xl">
        <div><Label className="mb-1.5 block">Property Name</Label><Input data-testid="setting-name" value={s.property_name} onChange={(e) => set("property_name", e.target.value)} /></div>
        <div><Label className="mb-1.5 block">Tagline</Label><Input data-testid="setting-tagline" value={s.tagline} onChange={(e) => set("tagline", e.target.value)} /></div>
        <div><Label className="mb-1.5 block">Logo <span className="text-brand-forest/40">(shown on the home hero in place of the tagline)</span></Label>
          <MediaUpload testid="setting-logo" value={s.logo_url} onUploaded={(d) => set("logo_url", d.url)} onClear={() => set("logo_url", "")} />
        </div>
        <div><Label className="mb-1.5 block">Welcome Message</Label><Textarea data-testid="setting-welcome" rows={4} value={s.welcome_message} onChange={(e) => set("welcome_message", e.target.value)} /></div>
        <div><Label className="mb-1.5 block">Home Hero Image</Label>
          <MediaUpload testid="setting-hero" value={s.hero_image} onUploaded={(d) => set("hero_image", d.url)} onClear={() => set("hero_image", "")} />
        </div>
        <div><Label className="mb-1.5 block">Notification Email <span className="text-brand-forest/40">(where guest requests are sent)</span></Label>
          <Input data-testid="setting-email" value={s.owner_email} onChange={(e) => set("owner_email", e.target.value)} />
        </div>
        <div><Label className="mb-1.5 block">Notification Phone Number <span className="text-brand-forest/40">(text alerts, in addition to email)</span></Label>
          <Input data-testid="setting-phone" value={s.owner_phone || ""} onChange={(e) => set("owner_phone", e.target.value)} placeholder="+12395551234" />
        </div>
        <div className="border-t border-black/5 pt-5">
          <Label className="mb-1.5 block">Second recipient <span className="text-brand-forest/40">(optional)</span></Label>
          <p className="text-sm text-brand-forest/60 mb-3">Add a partner or co-host to also receive owner alert emails and texts.</p>
          <Input data-testid="setting-email-2" value={s.owner_email_2 || ""} onChange={(e) => set("owner_email_2", e.target.value)} placeholder="partner@example.com" className="mb-3" />
          <Input data-testid="setting-phone-2" value={s.owner_phone_2 || ""} onChange={(e) => set("owner_phone_2", e.target.value)} placeholder="+14805551234  (mobile, for text alerts)" />
        </div>
        <div><Label className="mb-1.5 block">Public site address <span className="text-brand-forest/40">(used by every printed QR code — set to your live domain)</span></Label>
          <Input data-testid="setting-public-url" value={s.public_site_url || ""} onChange={(e) => set("public_site_url", e.target.value)} placeholder="https://guidebook.myazbnb.com" />
          <p className="text-xs text-brand-forest/50 mt-1.5">Leave blank to use whatever address you're currently on. Set it to your live domain so QR codes always point to the deployed site.</p>
        </div>
        <div><Label className="mb-1.5 block">Default door code <span className="text-brand-forest/40">(auto-filled for new & imported reservations unless you change a guest's code)</span></Label>
          <Input data-testid="setting-default-door-code" value={s.default_door_code ?? "4524"} onChange={(e) => set("default_door_code", e.target.value)} placeholder="4524" />
          <p className="text-xs text-brand-forest/50 mt-1.5">Every guest imported from Gmail (and new manual reservations) starts with this code. Override any individual guest in the Reservations table.</p>
        </div>
        <div><Label className="mb-1.5 block">myAZbnb Admin URL <span className="text-brand-forest/40">(the "myAZbnb Admin" sidebar button links here)</span></Label>
          <Input data-testid="setting-main-admin-url" value={s.main_site_admin_url || ""} onChange={(e) => set("main_site_admin_url", e.target.value)} placeholder="https://myazbnb.com/admin" />
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm max-w-2xl mt-6" data-testid="screensaver-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl tracking-tight">Idle Ad Screensaver</h2>
            <p className="text-sm text-brand-forest/60 mt-1">When the tablet sits idle, show a full-screen loop of your add-ons to promote them. Guests tap anywhere to jump to the Add-Ons page.</p>
          </div>
          <Switch data-testid="setting-screensaver" checked={s.screensaver_enabled !== false} onCheckedChange={(v) => set("screensaver_enabled", v)} />
        </div>
        <div className="mt-5 max-w-xs">
          <Label className="mb-1.5 block">Start after (minutes idle)</Label>
          <Input data-testid="setting-idle-minutes" type="number" min="1" value={s.idle_minutes ?? 3} onChange={(e) => set("idle_minutes", parseInt(e.target.value) || 1)} />
        </div>
        <p className="text-xs text-brand-forest/50 mt-3">Tip: add a Promo Video to any add-on (in the Add-Ons tab) and it will play on a loop here. Shows on tablet & desktop only.</p>
        <div className="mt-5 border-t border-black/5 pt-5">
          <Label className="mb-1.5 block">Home Screensaver Image <span className="text-brand-forest/40">(shown full-screen as the first slide — a finished banner with its own text works best)</span></Label>
          <MediaUpload testid="setting-screensaver-image" value={s.screensaver_image} onUploaded={(d) => set("screensaver_image", d.url)} onClear={() => set("screensaver_image", "")} />
          <p className="text-xs text-brand-forest/50 mt-1.5">When set, the home slide shows this image only (no overlay text). Leave blank to use the property name + hero image instead.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm max-w-2xl mt-6" data-testid="reminder-sound-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl tracking-tight">Reminder Sound</h2>
            <p className="text-sm text-brand-forest/60 mt-1">Play a gentle chime while a full-screen reminder is on the tablet. It repeats every 30 seconds until a guest taps the screen to dismiss it.</p>
          </div>
          <Switch data-testid="setting-reminder-sound" checked={s.reminder_sound_enabled !== false} onCheckedChange={(v) => set("reminder_sound_enabled", v)} />
        </div>
        <Button type="button" onClick={previewChime} data-testid="preview-reminder-sound" className="mt-4 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white transition-colors">
          <Volume2 size={16} className="mr-2" /> Preview sound
        </Button>
        <p className="text-xs text-brand-forest/50 mt-3">Note: phone/tablet browsers only allow sound after the guest has tapped the screen at least once, so the chime starts once the app has been touched.</p>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm max-w-2xl mt-6" data-testid="qr-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-brand-sand flex items-center justify-center">
            <QrCode size={22} className="text-brand-terracotta" />
          </div>
          <div>
            <h2 className="font-display text-xl tracking-tight">Guest QR Access</h2>
            <p className="text-sm text-brand-forest/60">Print this and place it in the home — guests scan to open the guidebook on their phone.</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div ref={qrRef} className="p-4 bg-white rounded-2xl border border-black/5" data-testid="qr-code">
            <QRCodeCanvas value={guestUrl} size={168} fgColor="#2C3B2E" bgColor="#ffffff" level="M" includeMargin />
          </div>
          <div className="flex-1 w-full">
            <Label className="mb-1.5 block">Guidebook Link</Label>
            <div className="flex gap-2">
              <Input readOnly value={guestUrl} data-testid="qr-url" className="bg-brand-bg" />
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(guestUrl); toast.success("Link copied"); }} data-testid="qr-copy" className="rounded-full shrink-0">
                <Copy size={16} />
              </Button>
            </div>
            <Button onClick={printQR} data-testid="qr-print" className="mt-4 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white active:scale-95 transition-colors">
              <Printer size={16} className="mr-2" /> Print QR Card
            </Button>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm max-w-2xl mt-6" data-testid="account-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-brand-sand flex items-center justify-center">
            <KeyRound size={22} className="text-brand-terracotta" />
          </div>
          <div>
            <h2 className="font-display text-xl tracking-tight">Account &amp; Login</h2>
            <p className="text-sm text-brand-forest/60">Change the email and password you use to sign in to this admin dashboard.</p>
          </div>
        </div>
        <div className="space-y-5">
          <div>
            <Label className="mb-1.5 block">Current Password <span className="text-brand-forest/40">(required to save changes)</span></Label>
            <Input data-testid="account-current-password" type="password" autoComplete="current-password" value={cred.current_password} onChange={(e) => setC("current_password", e.target.value)} placeholder="Enter your current password" />
          </div>
          <div>
            <Label className="mb-1.5 block">Login Email</Label>
            <Input data-testid="account-new-email" type="email" autoComplete="username" value={cred.new_email} onChange={(e) => setC("new_email", e.target.value)} placeholder="you@example.com" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">New Password <span className="text-brand-forest/40">(optional)</span></Label>
              <Input data-testid="account-new-password" type="password" autoComplete="new-password" value={cred.new_password} onChange={(e) => setC("new_password", e.target.value)} placeholder="Leave blank to keep current" />
            </div>
            <div>
              <Label className="mb-1.5 block">Confirm New Password</Label>
              <Input data-testid="account-confirm-password" type="password" autoComplete="new-password" value={cred.confirm_password} onChange={(e) => setC("confirm_password", e.target.value)} placeholder="Re-enter new password" />
            </div>
          </div>
          <Button onClick={saveCredentials} disabled={savingCred} data-testid="account-save" className="rounded-full bg-brand-forest hover:bg-brand-terracotta text-white active:scale-95 transition-colors">
            <KeyRound size={16} className="mr-2" /> {savingCred ? "Updating…" : "Update Login"}
          </Button>
        </div>
      </div>
    </div>
  );
}
